import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  DISMISS_SCROLLING_NOTIFICATION,
  LOG_SCROLLING_NOTIFICATION_IMPRESSION
} from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';

import {
  MessageRuntime,
  pickNextMessage,
  preserveCurrent,
  recordDisplay,
  SchedulerMessage,
  timeUntilNext
} from './tickerScheduler';
import { setTickerVisibleForContext } from './tickerVisibility';

const AVG_CHAR_WIDTH_PX = 8;

// Readability scale on the configured scroll_speed. Production testing found the
// raw scroll_speed (chars/sec × px/char) ran the marquee too fast to read; this
// factor slows it proportionally so players can finish a message before it exits,
// while still respecting relative per-campaign speed differences.
const READABILITY_SPEED_SCALE = 0.6;
// Hard ceiling on marquee speed (px/sec) so an aggressively-configured campaign
// can't outrun comfortable reading regardless of the scale above.
const MAX_SCROLL_PX_PER_SEC = 70;

type CampaignMessage = SchedulerMessage & {
  message: string;
  scrollSpeed: number;
  clickActionType?: string;
  clickActionValue?: string;
};

type Props = {
  context?: 'lobby' | 'table';
  onFinish?: () => void;
  // Reports whether the banner is actually showing a message right now (false
  // when paused for a modal, dismissed, or between messages). Lets a parent
  // reserve space for the ticker only while it's genuinely visible.
  onVisibilityChange?: (visible: boolean) => void;
  noHeightAnimation?: boolean;
  // 'default' = legacy dark/navy treatment used in table + grid views.
  // 'outlawSolid' = Skullcrusher Outlaw Solid: orange gradient with kerosene
  // noise overlay, dashed bottom rail, Oswald uppercase. Used in SaloonLobby.
  variant?: 'default' | 'outlawSolid';
};

// Per-message runtime (display count + cooldown), kept OUTSIDE the component so
// lobby/table remounts and multiple ticker instances share one schedule rather
// than resetting a message's repeat count / interval. Session-dismissed campaign
// ids are likewise module-level so a dismiss sticks until refresh.
const messageRuntime = new Map<string, MessageRuntime>();
// Marquee position, preserved across mounts. The table header (and its ticker)
// lives inside each table's panel, so switching tables mounts a NEW ticker -
// without this the message visibly restarted from the right edge on every
// swipe. The scroll effectively PAUSES during the switch and resumes exactly
// where it left off. TTL'd so a long-dead position never resurrects.
const marqueeResume = new Map<string, { progress: number; at: number }>();
const MARQUEE_RESUME_TTL_MS = 10_000;
// Whether the bar's height-entrance has played, per context - module-level for
// the same reason as marqueeResume: a table switch mounts a NEW ticker, and a
// per-instance flag replayed the 0->34px entrance on every switch, which is
// the "header flashes for a second" glitch. The entrance is for the bar
// APPEARING in a session, not for every remount of the same bar.
const entrancePlayedByContext = new Set<string>();
const dismissedCampaigns = new Set<number>();
const runtimeListeners = new Set<() => void>();

const notifyRuntimeChanged = () => {
  runtimeListeners.forEach((listener) => listener());
};

const TickerBanner = ({
  context = 'lobby',
  onFinish,
  onVisibilityChange,
  noHeightAnimation = false,
  variant = 'default'
}: Props) => {
  const { socket } = useContext(SocketContext);
  const campaigns = useAppSelector((state) => (state as any).notifications?.campaigns || []);
  // Hold off displaying / rotating campaign messages while the auto-prompted
  // Promo Entry Modal is open, so the player doesn't miss messages advancing
  // behind it. Rotation resumes once the modal is dismissed (Unity parity).
  const promoEntryModalOpen = useAppSelector(
    (state) => (state as any).playerPromos?.promoEntryModalOpen
  );
  // Same hold for the one-time multi-run introduction (EN-10791), which takes
  // the promo's place on the session it shows.
  const featureIntroModalOpen = useAppSelector(
    (state) => (state as any).playerPromos?.featureIntroModalOpen
  );

  // `cycle` is bumped to re-evaluate which message to show — after a message
  // finishes, when a cooldown elapses, or when another instance changes the
  // shared runtime (dismiss / cross-instance sync).
  const [cycle, setCycle] = useState(0);
  const [resizeVersion, setResizeVersion] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLSpanElement>(null);
  const glowRef = useRef<HTMLSpanElement>(null);
  const pulseOverlayRef = useRef<HTMLSpanElement>(null);
  const enteredRef = useRef(entrancePlayedByContext.has(context));
  const tlRef = useRef<gsap.core.Timeline | gsap.core.Tween | null>(null);
  const glowTlRef = useRef<gsap.core.Timeline | null>(null);
  const loggedImpressionsRef = useRef<Set<string>>(new Set());

  // Re-evaluate whenever another mount updates the shared runtime (e.g. a
  // dismiss, or a message going on cooldown in the other view).
  useEffect(() => {
    const listener = () => setCycle((prev) => prev + 1);
    runtimeListeners.add(listener);
    return () => {
      runtimeListeners.delete(listener);
    };
  }, []);

  // The pool of messages eligible for THIS context: not paused, not dismissed,
  // context-matched, non-empty text. Rebuilt on `cycle` so a dismiss (which
  // mutates the module-level dismissed set) drops those messages out.
  const pool = useMemo<CampaignMessage[]>(() => {
    const out: CampaignMessage[] = [];
    // Monotonic index capturing the admin/server delivery order across all
    // campaigns → messages. Used as the ordering tie-break so first-display
    // order matches Admin even when sort_order is absent or tied — admin
    // reordering doesn't change message ids, so id order ≠ admin order.
    let seq = 0;
    (campaigns as any[]).forEach((campaign: any) => {
      if (campaign.rotation_paused) return;
      const campaignId = campaign.campaign_id || campaign.campaignId || 0;
      if (dismissedCampaigns.has(campaignId)) return;
      if (
        campaign.contexts &&
        !campaign.contexts.includes(context) &&
        !campaign.contexts.includes('all')
      ) {
        return;
      }
      (campaign.messages || []).forEach((msg: any) => {
        const text = (msg.message || '').trim();
        if (!text) return;
        out.push({
          campaignId,
          id: msg.id,
          message: text,
          scrollSpeed: msg.scroll_speed || msg.scrollSpeed || 12,
          // Coerce so a numeric-string sort_order still sorts numerically, and
          // an explicit 0 (first slot) is preserved rather than skipped.
          sortOrder: Number(msg.sort_order ?? msg.sortOrder ?? 0) || 0,
          seq: seq++,
          // repeat_instance = repeat count; repeat_interval is in SECONDS.
          repeatInstance: msg.repeat_instance ?? msg.repeatInstance ?? 1,
          repeatIntervalMs:
            Math.max(0, Number(msg.repeat_interval ?? msg.repeatInterval ?? 0)) * 1000,
          clickActionType: msg.click_action_type || msg.clickActionType,
          clickActionValue: msg.click_action_value || msg.clickActionValue
        });
      });
    });
    // Campaign ID first so each campaign's messages stay grouped (sort_order is
    // per-campaign — starting at 0 in every campaign — so sorting globally by it
    // interleaved campaigns). Then the admin's per-campaign sort_order, then the
    // delivered sequence for ties/missing values — never message id.
    out.sort((a, b) => a.campaignId - b.campaignId || a.sortOrder - b.sortOrder || a.seq - b.seq);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, context, cycle]);

  const poolSignature = useMemo(
    () => pool.map((message) => `${message.campaignId}:${message.id}`).join('|'),
    [pool]
  );

  const paused = !!promoEntryModalOpen || !!featureIntroModalOpen || pool.length === 0;

  // Seeded SYNCHRONOUSLY at mount rather than left null for the selection
  // effect to fill in. A table switch REMOUNTS this whole component (the
  // header lives in each table's panel), and a first render with no current
  // message renders no bar at all for that tick - the header's floating
  // clusters lay out unshifted and then jump back down when the message
  // arrives a moment later. On device that reads as the header "moving" on
  // every swipe while the ticker is up. The runtime maps are module-level, so
  // the pick lands on the same message the outgoing instance was showing and
  // marqueeResume continues it mid-scroll.
  const [current, setCurrent] = useState<CampaignMessage | null>(() =>
    paused ? null : pickNextMessage(pool, messageRuntime, Date.now())
  );

  // Recalculate marquee travel on resize / orientation change. Only the viewport
  // WIDTH affects the scroll distance, so a resize that leaves the width
  // unchanged is ignored — mobile browsers fire 'resize' when the URL bar
  // shows/hides (height only), and recalculating there restarted the scroll from
  // the start mid-message (the reported glitch). Orientation always recalculates.
  useEffect(() => {
    let lastWidth = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      setResizeVersion((prev) => prev + 1);
    };
    const onOrientation = () => {
      lastWidth = window.innerWidth;
      setResizeVersion((prev) => prev + 1);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientation);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, []);

  // Selection: pick the message to show now, or schedule a wake when the soonest
  // one comes off cooldown. One message shows at a time (Unity parity).
  useEffect(() => {
    if (paused) {
      setCurrent(null);
      return;
    }
    const now = Date.now();
    const next = pickNextMessage(pool, messageRuntime, now);
    if (next) {
      // Keep the same object identity when the picked message is the one already
      // scrolling, so a re-selection (cross-instance sync, campaign refresh, or
      // the cooldown wake) doesn't restart the marquee from the start mid-message.
      setCurrent((prev) => preserveCurrent(prev, next));
      return;
    }
    // Nothing available right now — hide and wake when the soonest is ready.
    setCurrent(null);
    const wait = timeUntilNext(pool, messageRuntime, now);
    if (!Number.isFinite(wait)) return;
    const timer = window.setTimeout(() => setCycle((prev) => prev + 1), Math.max(50, wait));
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, poolSignature, cycle, pool]);

  // When the bar goes from SHOWING to hidden (message pool ran dry), replay
  // the height entrance the next time a message appears. Only a real
  // shown -> hidden transition arms the replay: running on the initial null
  // (as this once did) reset the flag on every remount, which replayed the
  // 0 -> 34px entrance on every table switch - the header visibly pumping.
  const hadCurrentRef = useRef(!!current);
  useEffect(() => {
    if (current) {
      hadCurrentRef.current = true;
      return;
    }
    if (hadCurrentRef.current) enteredRef.current = false;
    hadCurrentRef.current = false;
  }, [current]);

  // Log one impression per message (deduped for the session).
  useEffect(() => {
    if (!current || paused || !socket) return;
    const key = `${current.campaignId}:${current.id}`;
    if (loggedImpressionsRef.current.has(key)) return;
    loggedImpressionsRef.current.add(key);
    const playerGUID = localStorage.getItem('playerGUID') || '';
    socket.emit(LOG_SCROLLING_NOTIFICATION_IMPRESSION, {
      campaign_id: current.campaignId,
      message_id: current.id,
      playerGUID,
      player_id: playerGUID
    });
  }, [current, paused, socket]);

  const isTable = context === 'table';
  const isOutlaw = variant === 'outlawSolid';
  const shouldAnimateHeight = !noHeightAnimation && !isOutlaw;

  // Scroll the current message once, right edge → off the left, then record the
  // display (advancing its count + cooldown) and schedule the next.
  useLayoutEffect(() => {
    if (paused || !current) return;
    if (!containerRef.current || !trackRef.current || !copyRef.current) return;

    const banner = containerRef.current;
    const track = trackRef.current;
    const copy = copyRef.current;
    const shown = current;

    tlRef.current?.kill();
    gsap.killTweensOf(track);
    gsap.killTweensOf(banner);

    const charsPerSec = Number(shown.scrollSpeed) || 12;
    const speedPx = Math.max(
      1,
      Math.min(charsPerSec * AVG_CHAR_WIDTH_PX * READABILITY_SPEED_SCALE, MAX_SCROLL_PX_PER_SEC)
    );
    const messageWidth = copy.scrollWidth || shown.message.length * AVG_CHAR_WIDTH_PX;
    const reelWidth =
      (track.parentElement as HTMLElement)?.clientWidth || banner.clientWidth || messageWidth;

    const resumeKey = `${shown.campaignId}:${shown.id}`;
    const runScroll = () => {
      // Enter from the right edge (empty reel → first letter scrolls in), exit
      // fully off the left.
      gsap.set(track, { x: reelWidth });
      tlRef.current = gsap.to(track, {
        x: -messageWidth,
        duration: (reelWidth + messageWidth) / speedPx,
        ease: 'none',
        onComplete: () => {
          // Record the display (advances count + starts the admin-configured
          // repeat-interval cooldown), then re-evaluate. The gap before this (or
          // any) message shows again is governed SOLELY by that interval — no
          // artificial delay. notifyRuntimeChanged re-triggers this instance
          // (and syncs the other mounted ticker) to pick the next message.
          recordDisplay(pool, messageRuntime, shown, Date.now());
          notifyRuntimeChanged();
        }
      });
      // Resume mid-message if this instance replaced one that unmounted
      // mid-scroll (a table switch). Near-complete positions start fresh
      // instead - resuming at 99% would flash the tail and rotate instantly.
      const stored = marqueeResume.get(resumeKey);
      marqueeResume.delete(resumeKey);
      if (stored && Date.now() - stored.at < MARQUEE_RESUME_TTL_MS && stored.progress < 0.98) {
        tlRef.current.progress(stored.progress);
      }
    };

    // Height entrance the first time the bar appears after being hidden.
    if (shouldAnimateHeight && !enteredRef.current) {
      enteredRef.current = true;
      entrancePlayedByContext.add(context);
      const entryTl = gsap.timeline({ onComplete: runScroll });
      entryTl.fromTo(
        banner,
        { height: 0, opacity: 0 },
        { height: 34, opacity: 1, duration: 0.35, ease: 'power2.out' }
      );
      tlRef.current = entryTl;
    } else {
      if (shouldAnimateHeight) gsap.set(banner, { height: 34, opacity: 1 });
      runScroll();
    }

    return () => {
      // Bank the scroll position so the next mount (a table switch replaces
      // this whole instance) continues instead of restarting. Only a mid-
      // flight scroll is worth banking - entry timelines and finished scrolls
      // are not (a finished scroll's onComplete already rotated the message).
      const tl = tlRef.current;
      if (tl && typeof tl.progress === 'function') {
        const p = tl.progress();
        if (p > 0 && p < 0.98) {
          marqueeResume.set(resumeKey, { progress: p, at: Date.now() });
        }
      }
      tlRef.current?.kill();
      gsap.killTweensOf(track);
      gsap.killTweensOf(banner);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, resizeVersion, paused, shouldAnimateHeight]);

  // Decorative sweeping glow over the lead icon while a message is showing.
  useEffect(() => {
    if (!pulseOverlayRef.current || !glowRef.current || paused || !current) return;
    glowTlRef.current?.kill();
    gsap.set(pulseOverlayRef.current, { xPercent: -140, opacity: 0 });
    // Each sweep plays once and the next one is scheduled with setTimeout
    // instead of repeat:-1 + repeatDelay: an infinite timeline keeps GSAP's
    // ticker - and with it the WebView's whole frame pipeline - awake even
    // during the repeatDelay, producing refresh-rate frames of nothing. A
    // timer-scheduled restart lets the ticker sleep between sweeps. See
    // docs/investigations/20260819-game-client-overheating.md (8c/8d).
    let restartId: number | undefined;
    const tl = gsap
      .timeline({
        onComplete: () => {
          restartId = window.setTimeout(() => tl.restart(), 1400);
        }
      })
      .to(pulseOverlayRef.current, { opacity: 0.85, duration: 0.18, ease: 'power1.out' }, 0)
      .to(pulseOverlayRef.current, { xPercent: 335, duration: 1.35, ease: 'power2.inOut' }, 0)
      .to(pulseOverlayRef.current, { opacity: 0, duration: 0.25, ease: 'power1.in' }, 1.1);
    glowTlRef.current = tl;
    return () => {
      window.clearTimeout(restartId);
      glowTlRef.current?.kill();
    };
  }, [current, paused]);

  useEffect(() => {
    if (paused || !current) onFinish?.();
  }, [paused, current, onFinish]);

  // Report actual visibility so a parent can reserve space only while a message
  // is really on screen (mirrors the render guard below: paused || !current).
  // LAYOUT effect: the parent moves header clusters on this flag, and reporting
  // after paint gave one painted frame at the wrong position on every remount.
  // Also banked per-context (see visibleByContext) so a REMOUNTING parent can
  // seed its state with the truth and first-paint at the settled position.
  useLayoutEffect(() => {
    const visible = !paused && !!current;
    setTickerVisibleForContext(context, visible);
    onVisibilityChange?.(visible);
  }, [paused, current, context, onVisibilityChange]);

  // Cleanup must precede the replacement's layout report. A passive cleanup
  // runs AFTER the new banner reports true (e.g. inline → FixedChrome portal),
  // overwriting the action rail's state with false while the banner stays up.
  useLayoutEffect(() => () => onVisibilityChange?.(false), [onVisibilityChange]);

  const handleClick = useCallback(() => {
    if (current?.clickActionType === 'url' && current.clickActionValue) {
      window.open(current.clickActionValue, '_blank', 'noopener');
    }
  }, [current]);

  // Manual dismiss (X) is a SESSION dismiss of every currently-eligible campaign
  // (Unity's DismissAllCampaigns): they won't show again until refresh. Other
  // campaign ids that arrive later still show.
  const handleDismiss = useCallback(() => {
    tlRef.current?.kill();

    const dismissedIds = new Set(pool.map((message) => message.campaignId));
    dismissedIds.forEach((id) => dismissedCampaigns.add(id));

    if (socket) {
      const playerGUID = localStorage.getItem('playerGUID') || '';
      pool.forEach((message) => {
        socket.emit(DISMISS_SCROLLING_NOTIFICATION, {
          campaign_id: message.campaignId,
          message_id: message.id,
          playerGUID,
          player_id: playerGUID
        });
      });
    }

    notifyRuntimeChanged();
    setCurrent(null);
    setCycle((prev) => prev + 1);
  }, [pool, socket]);

  if (paused || !current) return null;

  const isClickable = !!(current.clickActionType && current.clickActionValue);
  const bannerVariantStyle = isOutlaw ? outlawBannerStyle : bannerStyle;
  const fontSize = isOutlaw ? '15px' : isTable ? '0.82rem' : '0.9rem';
  const segmentTextStyle: CSSProperties = {
    ...(isOutlaw ? outlawMessageStyle : messageStyle),
    fontSize
  };

  return (
    <div
      ref={containerRef}
      style={{
        ...bannerVariantStyle,
        // First paint at FULL size when the entrance already played (a table
        // switch remounting the bar) - starting every remount at 0 height
        // shifted the header for a frame even though no entrance would run.
        height: shouldAnimateHeight && !enteredRef.current ? 0 : shouldAnimateHeight ? 34 : 'auto',
        opacity: shouldAnimateHeight && !enteredRef.current ? 0 : 1,
        cursor: isClickable ? 'pointer' : 'default'
      }}
      onClick={isClickable ? handleClick : undefined}>
      <span ref={pulseOverlayRef} aria-hidden='true' style={pulseOverlayStyle} />

      {isOutlaw && (
        <>
          <span aria-hidden style={outlawNoiseStyle} />
          <span aria-hidden style={outlawRailStyle} />
        </>
      )}

      {isOutlaw ? (
        <span ref={glowRef} style={outlawIconStyle} aria-hidden='true'>
          <svg viewBox='0 0 24 24' width='14' height='14' fill='currentColor'>
            <path d='M12 2a3 3 0 0 0-3 3v1H7.5A1.5 1.5 0 0 0 6 7.5V9H4v2h2v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9h2V9h-2V7.5A1.5 1.5 0 0 0 16.5 6H15V5a3 3 0 0 0-3-3zm-1 4V5a1 1 0 1 1 2 0v1h-2zm-2 6a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm5 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0z' />
          </svg>
        </span>
      ) : (
        <span ref={glowRef} style={iconStyle}>
          H
        </span>
      )}

      <div style={reelContainerStyle}>
        {/* Single message scrolls once, right → left. */}
        <div ref={trackRef} style={trackStyle}>
          <span ref={copyRef} style={segmentTextStyle}>
            {current.message}
          </span>
        </div>
      </div>

      <button
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDismiss();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDismiss();
        }}
        style={isOutlaw ? outlawDismissStyle : dismissStyle}>
        <CloseIcon style={{ fontSize: '0.8rem' }} />
      </button>
    </div>
  );
};

export default TickerBanner;

const bannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: 42,
  background:
    'linear-gradient(90deg, rgba(53, 34, 17, 0.98) 0%, rgba(22, 18, 14, 0.98) 22%, rgba(22, 18, 14, 0.96) 100%)',
  borderTop: '1px solid rgba(233, 140, 38, 0.3)',
  borderBottom: '1px solid rgba(233, 140, 38, 0.48)',
  boxShadow:
    '0 10px 24px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.05), inset 0 -1px 0 rgba(233, 140, 38, 0.18)',
  padding: '0 1rem',
  gap: '0.8rem',
  overflow: 'hidden',
  flexShrink: 0,
  position: 'relative'
};

const iconStyle: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#E98C26',
  boxShadow: '0 0 0 4px rgba(233, 140, 38, 0.16), 0 0 18px rgba(233, 140, 38, 0.85)',
  flexShrink: 0,
  zIndex: 1
};

const pulseOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: '-20%',
  width: '42%',
  pointerEvents: 'none',
  opacity: 0,
  background:
    'linear-gradient(105deg, transparent 0%, rgba(233, 140, 38, 0.05) 26%, rgba(255, 214, 148, 0.28) 50%, rgba(233, 140, 38, 0.08) 74%, transparent 100%)',
  filter: 'blur(0.2px)',
  zIndex: 0
};

const reelContainerStyle: CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  zIndex: 1
};

// The scrolling track holds the single message on one line; whiteSpace nowrap
// keeps it measurable as one continuous width.
const trackStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'nowrap',
  flexShrink: 0,
  width: 'max-content',
  whiteSpace: 'nowrap',
  willChange: 'transform'
};

const messageStyle: CSSProperties = {
  // 700 (was 800): slightly lighter weight reads cleaner in motion than heavy
  // bold, and a touch of letter-spacing opens up the glyphs while scrolling.
  fontWeight: 700,
  whiteSpace: 'nowrap',
  letterSpacing: '0.02em',
  color: '#fff',
  textShadow: '0 1px 8px rgba(0, 0, 0, 0.42)',
  display: 'inline-block'
};

const dismissStyle: CSSProperties = {
  width: 30,
  height: 30,
  background: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  borderRadius: 6,
  color: 'rgba(255, 255, 255, 0.9)',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'color 0.15s ease',
  zIndex: 1
};

// ---- Outlaw Solid variant (Skullcrusher ticker.css .hjt-solid) ----
// Wall to wall, in the lobby as at the table (owner, 2026-09-17). This used to
// clip to `min(1480px, 100%)` and centre, to line up with the saloon header's
// amber stripe and the activity strip's dark backdrop — both of which were
// 1480px content-column objects. EN-10516 removed both: the header band and its
// dashed rail now run the full viewport width (SaloonHeader.module.scss) and
// the strip's backdrop is gone (ActivityStrip.module.scss), so the clip left
// the bar sitting inset under a band that no longer stops there. The table
// context already ran at 100%; the two contexts now share one box.
const outlawBannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: 34,
  background: 'linear-gradient(180deg, #f59838 0%, #e8821f 60%, #c66c12 100%)',
  borderBottom: '1px solid rgba(74, 35, 10, 0.55)',
  boxShadow: 'inset 0 1px 0 rgba(255, 180, 92, 0.25), 0 2px 6px rgba(0, 0, 0, 0.35)',
  padding: '0 6px 0 12px',
  gap: '0.5rem',
  overflow: 'hidden',
  flexShrink: 0,
  position: 'relative',
  boxSizing: 'border-box'
};

// Kerosene-noise overlay using inline SVG fractal noise. mix-blend-mode
// 'overlay' darkens highs and lightens lows so the orange retains depth.
const outlawNoiseStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='40'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.16 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
  mixBlendMode: 'overlay',
  opacity: 0.7
};

// Dashed bottom rail mirrors the lobby header's existing dashed accent.
const outlawRailStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 3,
  background: 'repeating-linear-gradient(90deg, rgba(74,35,10,0.85) 0 6px, transparent 6px 12px)',
  opacity: 0.55,
  pointerEvents: 'none',
  WebkitMaskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)',
  maskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)'
};

// Flat lock-glyph slot matching Skullcrusher .hjt-solid .hjt-lead. There is
// no badge background, just a centered 22px square in cream so the SVG reads
// as a small printed mark over the orange gradient.
const outlawIconStyle: CSSProperties = {
  flex: '0 0 auto',
  width: 22,
  height: 22,
  display: 'grid',
  placeItems: 'center',
  marginRight: 8,
  color: 'rgba(255, 240, 200, 0.95)',
  position: 'relative',
  zIndex: 1
};

const outlawMessageStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 600,
  // A little more tracking so the uppercase Oswald glyphs don't run together
  // while scrolling.
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  color: '#fff',
  textShadow: '0 1px 0 rgba(74, 35, 10, 0.5)',
  display: 'inline-block',
  position: 'relative',
  zIndex: 1
};

const outlawDismissStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(255, 240, 210, 0.95)',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'color 0.15s ease',
  position: 'relative',
  zIndex: 1
};
